import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Rocket } from "lucide-react";
import { bootstrapTenant, getMyTenantCount } from "@/lib/tenant-bootstrap.functions";
import { supabase } from "@/integrations/supabase/client";

/**
 * Self-serve tenant bootstrap. Any authenticated user lands here from /signup
 * (or from the empty state after first login) and provisions their workspace
 * in one form submit.
 */
export const Route = createFileRoute("/_authenticated/setup")({
  component: SetupPage,
  head: () => ({
    meta: [
      { title: "Set up your workspace — Cairn" },
      { name: "description", content: "Create your Cairn workspace and start onboarding." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function SetupPage() {
  const navigate = useNavigate();
  const bootstrap = useServerFn(bootstrapTenant);
  const fetchCount = useServerFn(getMyTenantCount);

  const { data: tenantInfo } = useQuery({
    queryKey: ["my-tenant-count"],
    queryFn: () => fetchCount(),
  });

  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const { tenantId } = await bootstrap({ data: { name: name.trim() } });
      // Refresh the JWT so the new tenant_admin role is in the user's claims
      // before they hit any RLS-gated query in the app.
      await supabase.auth.refreshSession();
      toast.success("Workspace created");
      void tenantId;
      navigate({ to: "/app/implementation" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create workspace");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full bg-primary/10 grid place-items-center">
              <Rocket className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Create Your Workspace</CardTitle>
              <CardDescription>One tenant per legal operating entity.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Workspace name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Fulfillment"
                maxLength={120}
                required
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                You will be the first admin. Add facilities, customers, and SKUs in the
                next step.
              </p>
            </div>
            {tenantInfo ? (
              <p className="text-xs text-muted-foreground">
                You administer {tenantInfo.count} of {tenantInfo.limit} allowed workspaces.
              </p>
            ) : null}
            <Button
              type="submit"
              className="w-full"
              disabled={submitting || !name.trim() || (tenantInfo && tenantInfo.count >= tenantInfo.limit)}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create workspace
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}